import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { ViewContentComponent } from './view-content/view-content.component';
import {
  FormGroup,
  FormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { takeUntil, debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { UniversalSearchService } from '../../../universal-search/services/universal-search.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AdminService } from '../../admin.service';
import { DateTime } from 'luxon';
import { saveAs } from '@progress/kendo-file-saver';
import { Content } from '../../../models/content-management-models';

@Component({
  selector: 'app-content-history',
  imports: [
    KENDO_GRID,
    MatIconModule,
    MatButtonModule,
    ViewContentComponent,
    MatSelectModule,
    MatAutocompleteModule,
    MatInputModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatAutocompleteModule,
    CommonModule,
    DatePipe,
  ],
  templateUrl: './content-history.component.html',
  styleUrl: './content-history.component.scss',
  providers: [],
})
export class ContentHistoryComponent implements OnInit {
  private usServ = inject(UniversalSearchService);
  private adminService = inject(AdminService);

  @Output() contentEmitter = new EventEmitter();
  viewMode: 'history' | 'content' = 'history';
  historyColConfig = [
    { field: 'id', title: 'History ID', type: 'text' },
    { field: 'unitId', title: 'Unit', type: 'text' },
    { field: 'userId', title: 'User', type: 'text' },
    { field: 'dateUpdated', title: 'Date Updated', type: 'date' },
    { field: 'type', title: 'Content Type', type: 'text' },
    { field: 'name', title: 'Name', type: 'text' },
  ];
  contentTypes = [
    { value: 'Disclosures', name: 'Disclosures' },
    { value: 'AiPrompt', name: 'AI Prompt' },
    { value: 'TermsAndDefinitions', name: 'Terms and Definitions' },
  ];
  units = [];
  historyData: Content[] = [];
  viewItem: string | null = null;
  viewId: number | string | null = null;
  contentForm: FormGroup;
  selectedUnit: { value: string; display: string };
  unsubscribe = new Subject();
  viewItemInfo: Content;
  currentContent: Content;

  ngOnInit() {
    this.viewMode = 'history';
    this.contentForm = new FormGroup({
      unit: new FormControl('', Validators.required),
      contentType: new FormControl(''),
      startDate: new FormControl(''),
      endDate: new FormControl(''),
    });

    this.contentForm.controls['unit'].valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(value => {
        if (typeof value === 'string') {
          this.adminService.getUnitList(value, true).subscribe(resp => {
            const newUnitsList = resp.names.map(unitRes => ({
              value: unitRes.id,
              display: unitRes.name,
            }));
            this.units = newUnitsList;
          });
        }
      });
  }

  onViewContent(recordItem) {
    this.viewMode = 'content';
    this.viewId = recordItem.id;
    this.viewItemInfo = recordItem;
  }

  onViewBack(): void {
    this.viewMode = 'history';
    this.viewItem = null;
  }

  onExportContent(recordItem) {
    this.onGetRecord(recordItem, true);
  }

  onViewCopy(): void {
    const emitData = {
      unit: this.selectedUnit,
      content: this.viewItemInfo.content,
      contentType: this.viewItemInfo.type,
      name: this.viewItemInfo.name,
    };
    this.contentEmitter.emit(emitData);
  }

  onSearchHistory() {
    this.currentContent = null;
    const payloadData0 = {
      unitId: this.contentForm.value.unit.value,
      type: this.contentForm.value.contentType,
    };
    this.adminService
      .getContent(payloadData0)
      .subscribe(res => (this.currentContent = res));
    const payloadData1 = {
      unitId: this.contentForm.value.unit.value,
      type: this.contentForm.value.contentType,
      start: DateTime.fromJSDate(
        new Date(new Date(this.contentForm.value.startDate))
      )
        .startOf('day')
        .toUTC()
        .toISO(),
      end: DateTime.fromJSDate(
        new Date(new Date(this.contentForm.value.endDate))
      )
        .endOf('day')
        .toUTC()
        .toISO(),
    };
    this.adminService
      .getContentHistory(payloadData1)
      .subscribe(res => (this.historyData = res));
  }

  onGetRecord(recordItem, download?) {
    if (download) {
      this.adminService
        .getContentRecordDownload(recordItem.id)
        .subscribe(res => {
          let fileData;
          const reader = new FileReader();
          reader.readAsDataURL(res);

          reader.onloadend = () => {
            fileData = reader.result;
            saveAs(fileData, `${recordItem.dateUpdated.split('T')[0]}_Content`);
          };
        });
    } else {
      this.adminService
        .getContentRecord(recordItem.id)
        .subscribe(res => (this.viewItem = res.content));
    }
  }

  changeUnitSelection(ev) {
    this.selectedUnit = ev.option.value;
  }

  displayFn(ev) {
    return ev.display;
  }
}
